/**
 * Attendance statistics + calendar subscription (ICS feed) hooks.
 *
 * Query keys: ['stats-attendance', from, to], ['stats-member', userId, from, to], ['stats-me'],
 * ['cal-feed']. None of them is persisted (stats are editor data and change after every rehearsal;
 * the feed status needs the server anyway).
 */
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AttendanceStatsDTO,
  CalendarFeedLinkDTO,
  CalendarFeedStatusDTO,
  DateRange,
  MemberAttendanceDetailDTO,
} from '../../shared/dto/stats';
import { api } from './client';

export const statsKey = (from: string, to: string) => ['stats-attendance', from, to] as const;
export const memberStatsKey = (userId: string, from: string, to: string) => ['stats-member', userId, from, to] as const;
export const MY_STATS_KEY = ['stats-me'] as const;
export const CAL_FEED_KEY = ['cal-feed'] as const;

const rangeQuery = (r: DateRange) => `?from=${encodeURIComponent(r.from)}&to=${encodeURIComponent(r.to)}`;

/** GET /stats/attendance (editors). `range` null = not ready (e.g. an invalid custom range). */
export function useAttendanceStats(range: DateRange | null) {
  return useQuery({
    queryKey: statsKey(range?.from ?? '', range?.to ?? ''),
    queryFn: ({ signal }) => api<AttendanceStatsDTO>(`/stats/attendance${rangeQuery(range!)}`, { signal }),
    enabled: range !== null,
    staleTime: 60_000,
    // Keep the previous season on screen while another one loads.
    placeholderData: keepPreviousData,
  });
}

/** GET /stats/attendance/:userId (editors, or the member themself). */
export function useMemberAttendance(userId: string | null, range: DateRange | null) {
  return useQuery({
    queryKey: memberStatsKey(userId ?? '', range?.from ?? '', range?.to ?? ''),
    queryFn: ({ signal }) => api<MemberAttendanceDetailDTO>(`/stats/attendance/${encodeURIComponent(userId!)}${rangeQuery(range!)}`, { signal }),
    enabled: userId !== null && range !== null,
    staleTime: 60_000,
  });
}

/** GET /stats/me — the signed-in member's attendance this season. */
export function useMyAttendance(enabled = true) {
  return useQuery({
    queryKey: MY_STATS_KEY,
    queryFn: ({ signal }) => api<MemberAttendanceDetailDTO>('/stats/me', { signal }),
    enabled,
    staleTime: 5 * 60_000,
  });
}

/** GET /cal/token — is a calendar subscription link active? */
export function useCalendarFeedStatus() {
  return useQuery({
    queryKey: CAL_FEED_KEY,
    queryFn: ({ signal }) => api<CalendarFeedStatusDTO>('/cal/token', { signal }),
    staleTime: 60_000,
  });
}

/** POST /cal/token — a new link (the previous one stops working). The URL is only returned here. */
export function useCreateCalendarFeed() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api<CalendarFeedLinkDTO>('/cal/token', { method: 'POST', body: {} }),
    onSuccess: () => qc.setQueryData<CalendarFeedStatusDTO>(CAL_FEED_KEY, { active: true }),
  });
}

/** DELETE /cal/token — stop the subscription. */
export function useDeleteCalendarFeed() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api<CalendarFeedStatusDTO>('/cal/token', { method: 'DELETE' }),
    onSuccess: () => qc.setQueryData<CalendarFeedStatusDTO>(CAL_FEED_KEY, { active: false }),
  });
}
